import React, { useState, useEffect, useRef } from 'react';
import { EmptyStateIcon } from './NavIcons';
import BRAND from '../brand';
import { useLegalLinks } from '../legal';
import { api } from '../api';
import { openLink as platformOpenLink, getPlatform, getDevicePlatform, getWebAppUser } from '../platform';
import { payViaTgInvoice } from '../payments';
import { trackCertificatePurchaseStarted, trackCertificatePurchased } from '../utils/metrika';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatPhone(digits) {
  if (!digits || digits.length === 0) return '+7';
  let d = String(digits).replace(/\D/g, '');
  if (d.startsWith('8')) d = '7' + d.slice(1);
  if (d.length > 0 && !d.startsWith('7')) d = '7' + d;

  let r = '+7';
  if (d.length > 1) r += ' (' + d.slice(1, 4);
  if (d.length >= 4) r += ') ';
  if (d.length > 4) r += d.slice(4, 7);
  if (d.length > 7) r += '-' + d.slice(7, 9);
  if (d.length > 9) r += '-' + d.slice(9, 11);
  return r;
}

function parsePhoneDigits(value, prev) {
  let d = String(value).replace(/\D/g, '');
  if (d.startsWith('8')) d = '7' + d.slice(1);
  if (d.length > 0 && !d.startsWith('7')) d = '7' + d;
  d = d.slice(0, 11);
  if (prev && d === prev && value.length < formatPhone(prev).length) {
    d = d.slice(0, -1);
  }
  return d;
}

function BackButton({ onBack }) {
  return (
    <button className="checkout-back" onClick={onBack} type="button">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
      </svg>
    </button>
  );
}

function CertificatePurchasePage({ onBack, onPurchased, goToOrders }) {
  const legal = useLegalLinks();
  const [settings, setSettings] = useState({ min_amount: 3000, max_amount: 50000 });
  const [amount, setAmount] = useState('');
  const [senderName, setSenderName] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [message, setMessage] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const amountInputRef = useRef(null);

  useEffect(() => {
    // Контейнер .cert-checkout-root имеет собственный скролл (position: fixed
    // + overflow-y: auto, см. ниже в return), но на iOS/Max body может остаться
    // проскроллен от родителя — сбрасываем явно для подстраховки.
    try { window.scrollTo(0, 0); } catch {}
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    // Автофокус на поле суммы — с задержкой и preventScroll, чтобы клавиатура
    // в Max не подняла viewport.
    setTimeout(() => {
      try { amountInputRef.current?.focus({ preventScroll: true }); } catch {}
    }, 400);

    api.getCertificateSettings().then(s => setSettings(s)).catch(() => {});

    api.getCertificateBuyerProfile().then(p => {
      if (p?.first_name) {
        setSenderName(prev => prev || p.first_name);
      } else {
        const wu = getWebAppUser();
        if (wu?.first_name) setSenderName(prev => prev || wu.first_name);
      }
      if (p?.phone) {
        const digits = String(p.phone).replace(/\D/g, '');
        setPhone(prev => prev || (digits.length >= 10 ? digits.slice(-11).padStart(11, '7') : ''));
      }
    }).catch(() => {
      const wu = getWebAppUser();
      if (wu?.first_name) setSenderName(prev => prev || wu.first_name);
    });
  }, []);

  const platform = getPlatform();

  // Изолированный scroll-контейнер: высота = весь экран минус нижняя навигация.
  // Это гарантирует что при mount компонента scrollTop=0 (а не наследует
  // позицию body от ProfilePage). Стиль одинаковый для web и mini-app.
  const scrollWrapStyle = {
    height: 'calc(100dvh - var(--nav-height) - var(--safe-area-bottom))',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    background: 'white',
  };

  if (platform === 'web') {
    return (
      <div style={scrollWrapStyle}>
        <div className="checkout-page">
          <div className="checkout-header">
            <BackButton onBack={onBack} />
            <h2>Подарочный сертификат</h2>
          </div>
          <div className="checkout-form" style={{ textAlign: 'center', padding: '32px 20px' }}>
            <div style={{ marginBottom: 16 }}><EmptyStateIcon name="gift" size={48} /></div>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              Покупка сертификатов доступна в приложении {BRAND.name} в Telegram или Max.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handlePhoneChange = (value) => {
    if (phoneError) setPhoneError('');
    setPhone(parsePhoneDigits(value, phone));
  };

  const handleEmailChange = (value) => {
    if (emailError) setEmailError('');
    setEmail(value);
  };

  // Подсветка поля + плавный скролл и фокус (паттерн из CheckoutModal).
  // Для пустых полей сообщение не показываем — только подсветка; для невалидных
  // (телефон/email с маской) — текст подсказки в setError.
  const highlightField = (fieldId) => {
    const el = document.getElementById(fieldId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => {
      const input = el.querySelector('input, textarea');
      if (input) {
        input.style.transition = 'background 0.3s';
        input.style.background = '#FFE0E0';
        try { input.focus(); } catch {}
        setTimeout(() => {
          input.style.background = '';
          setTimeout(() => { input.style.transition = ''; }, 300);
        }, 1000);
      }
    }, 400);
  };

  const handleSubmit = async () => {
    setError('');
    setPhoneError('');
    setEmailError('');
    const amountNum = parseInt(amount, 10);
    if (!amountNum || isNaN(amountNum) || amountNum < settings.min_amount || amountNum > settings.max_amount) {
      setError(`Введите сумму от ${settings.min_amount.toLocaleString('ru-RU')} до ${settings.max_amount.toLocaleString('ru-RU')} ₽`);
      highlightField('field-cert-amount');
      return;
    }
    // Телефон/email: пустые — только подсветка; невалидный формат — inline-подсказка
    // под полем (не сверху формы — её не видно после скролла к полю)
    if (!phone) {
      highlightField('field-cert-phone');
      return;
    }
    if (phone.length !== 11) {
      setPhoneError('Введите корректный телефон');
      highlightField('field-cert-phone');
      return;
    }
    if (!email) {
      highlightField('field-cert-email');
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setEmailError('Введите корректный e-mail');
      highlightField('field-cert-email');
      return;
    }
    if (message.length > 300) {
      setError('Поздравление слишком длинное (макс. 300 символов)');
      return;
    }

    setIsLoading(true);
    trackCertificatePurchaseStarted(amountNum);
    try {
      const wu = getWebAppUser() || {};
      const payload = {
        amount: amountNum,
        sender_name: senderName.trim() || null,
        recipient_name: recipientName.trim() || null,
        message: message.trim() || null,
        source_platform: platform,
        device_platform: getDevicePlatform() || 'web',
        telegram_id: platform === 'telegram' && wu.id ? String(wu.id) : null,
        max_id: platform === 'max' && wu.id ? String(wu.id) : null,
        first_name: wu.first_name || null,
        last_name: wu.last_name || null,
        username: wu.username || null,
        phone: formatPhone(phone),
        email: email.trim(),
        consent_pd: true,
        consent_marketing: !!consentMarketing,
      };
      const result = await api.purchaseCertificate(payload);
      if (!result?.order_id) {
        throw new Error('Не удалось создать заказ');
      }
      // Нативный Telegram-инвойс — единый путь оплаты; итог придёт в
      // invoiceClosed → App.jsx (там же трекается «сертификат куплен» по
      // факту paid). false → redirect-флоу ниже.
      if (await payViaTgInvoice({ id: result.order_id, kind: 'cert', total: amountNum })) {
        onPurchased?.(result);
        if (typeof goToOrders === 'function') {
          goToOrders();
        } else {
          onBack?.();
        }
        return;
      }
      // Порядок:
      // 1) localStorage маркер — синхронно, ДО openLink (на iOS вэбвью при
      //    переходе в Safari может приостановить JS, код после openLink не
      //    выполнится → без маркера visibilitychange не покажет поп-ап).
      //    Cert использует отдельный ключ: id-пространства Order и
      //    GiftCertificate пересекаются, без маркера /payment/status находит
      //    Order первым и даёт ложный тост «Заказ №N».
      // 2) platformOpenLink — сразу (TG Desktop Mac требует свежий user-gesture).
      // 3) Остальное — после openLink (может не выполниться на iOS, но не критично).
      try {
        localStorage.setItem('stems_pending_payment_cert', String(result.order_id));
        localStorage.removeItem('stems_pending_payment');
      } catch (e) {}

      if (result.payment_url) {
        platformOpenLink(result.payment_url);
      }

      trackCertificatePurchased(amountNum, result.order_id);
      onPurchased?.(result);

      // Навигация в «Мои заказы» — даже если openLink не сработал, юзер
      // увидит cert в pending с кнопкой «Оплатить» для retry.
      if (typeof goToOrders === 'function') {
        goToOrders();
      } else {
        onBack?.();
      }
      if (!result.payment_url) {
        setError('Не удалось создать платёж в ЮКассе. Заказ сохранён в «Мои заказы» — попробуйте оплатить там.');
      }
    } catch (e) {
      setError(e.message || 'Ошибка покупки');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={scrollWrapStyle}>
      <div className="checkout-page">
        <div className="checkout-header">
          <BackButton onBack={onBack} />
          <h2>Подарочный сертификат</h2>
        </div>

        <div className="checkout-form">
        <div style={{
          background: '#FFF3E0',
          borderRadius: 10,
          padding: '12px 16px',
          marginBottom: 20,
          fontSize: 14,
          color: '#5D4037',
          lineHeight: 1.5,
        }}>
          Электронный сертификат придёт вам в чат от бота — в виде стильного цифрового сертификата с кодом активации. Перешлите его получателю: он пополнит баланс в {BRAND.name} и сам выберет понравившиеся стебли.
        </div>

        {error && <div className="checkout-error">{error}</div>}

        <div id="field-cert-amount" className="checkout-field">
          <label>Сумма, ₽ (от {settings.min_amount.toLocaleString('ru-RU')} до {settings.max_amount.toLocaleString('ru-RU')})</label>
          <input
            ref={amountInputRef}
            type="text"
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/\D/g, '').slice(0, 5))}
            placeholder="5000"
            maxLength={5}
          />
        </div>

        <div className="checkout-field">
          <label>От кого (необязательно)</label>
          <input
            type="text"
            value={senderName}
            onChange={(e) => setSenderName(e.target.value)}
            placeholder="Пётр"
            maxLength={200}
          />
        </div>

        <div className="checkout-field">
          <label>Для кого (необязательно)</label>
          <input
            type="text"
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            maxLength={200}
          />
        </div>

        <div className="checkout-field">
          <label>Текст пожелания (необязательно)</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 300))}
            placeholder="Напишите то, что хотели бы сказать"
            rows={3}
            style={{
              resize: 'none', minHeight: 80, width: '100%', boxSizing: 'border-box',
              padding: '14px 16px', fontSize: 15, border: '1px solid var(--border-color)',
              borderRadius: 12, background: 'var(--card-bg)', fontFamily: 'inherit',
              // Явно разрешаем выделение и iOS context-menu (long-press → copy/paste).
              // Без этого Max-вебвью блокирует context-menu по дефолту.
              WebkitUserSelect: 'text', userSelect: 'text',
              WebkitTouchCallout: 'default',
              touchAction: 'auto',
            }}
          />
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'right' }}>
            {message.length}/300
          </div>
        </div>

        <div id="field-cert-phone" className="checkout-field">
          <label>Телефон *</label>
          <input
            type="tel"
            value={formatPhone(phone)}
            onChange={(e) => handlePhoneChange(e.target.value)}
            placeholder="+7 (999) 888-22-22"
            maxLength={18}
          />
          {phoneError && (
            <div style={{ color: '#D32F2F', fontSize: 13, marginTop: 6 }}>{phoneError}</div>
          )}
        </div>

        <div id="field-cert-email" className="checkout-field">
          <label>E-mail *</label>
          <input
            type="email"
            value={email}
            onChange={(e) => handleEmailChange(e.target.value)}
            placeholder="example@mail.ru"
          />
          {emailError && (
            <div style={{ color: '#D32F2F', fontSize: 13, marginTop: 6 }}>{emailError}</div>
          )}
        </div>

        {/* Согласия — два чекбокса в стиле CheckoutModal: рекламные сообщения
            (опционально) и политика конфиденциальности (обязательно). */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', margin: '14px 0 6px' }}>
          <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '13px', lineHeight: '1.4', cursor: 'pointer', color: '#333' }}>
            <input
              type="checkbox"
              checked={consentMarketing}
              onChange={(e) => setConsentMarketing(e.target.checked)}
              style={{ marginTop: '2px', width: '16px', height: '16px', flexShrink: 0, cursor: 'pointer', accentColor: 'var(--primary-color)' }}
            />
            <span>Согласен получать рекламные и информационные сообщения</span>
          </label>
          <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '13px', lineHeight: '1.4', cursor: 'pointer', color: '#333' }}>
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              style={{ marginTop: '2px', width: '16px', height: '16px', flexShrink: 0, cursor: 'pointer', accentColor: 'var(--primary-color)' }}
            />
            <span>
              Я принимаю{' '}
              <a href={legal.policyUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1A73E8' }}>
                Политику конфиденциальности
              </a>
              {' '}и даю согласие на обработку персональных данных
            </span>
          </label>
        </div>

        <button
          type="button"
          className="checkout-submit"
          onClick={handleSubmit}
          disabled={isLoading || !consent}
        >
          {isLoading ? 'Создание...' : `Оплатить ${amount ? Number(amount).toLocaleString('ru-RU') : '0'} ₽`}
        </button>

        <p style={{
          fontSize: '10px', color: '#666', textAlign: 'center',
          margin: '8px 0 0', lineHeight: '1.4',
        }}>
          Нажимая «Оплатить», вы принимаете условия{' '}
          <a
            href={legal.ofertaUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#666', textDecoration: 'underline' }}
          >
            Публичной оферты
          </a>
        </p>
        </div>
      </div>
    </div>
  );
}

export default CertificatePurchasePage;
